/**
 * Phase 45 Plan 45-07 — Pick-something-up unassigned row.
 *
 * gridTemplateColumns LOCKED per UI-SPEC §Unassigned row grid spec:
 *   '8px 70px 1fr 120px 70px 80px 100px'
 *
 * Columns:
 *   1. Priority dot
 *   2. Task code (font-mono uppercase truncate)
 *   3. Task name (font-medium strong truncate)
 *   4. Project chip (project-dot + project short name)
 *   5. Priority label badge (uppercase)
 *   6. {est}h est · {age}
 *   7. Claim button (ms-orange bg) — disabled + "Claiming…" while isPending
 *
 * Claim button aria-label = `Claim {taskName}` per UI-SPEC §A11y Contract.
 */
import type { CSSProperties } from 'react';
import { PlusIcon } from '@heroicons/react/24/outline';
import type { UnassignedTaskDto } from '../../services/api/tasks';
import { MtPriorityBadge, priorityConfig } from './MtPriorityBadge';
import { useClaimTask } from '../../hooks/useClaimTask';

export const UNASSIGNED_ROW_GRID = '8px 70px 1fr 120px 70px 80px 100px';

export interface UnassignedRowProps {
  task: UnassignedTaskDto;
  density?: 'comfortable' | 'compact';
  /** Optional override for tests. */
  onClaim?: (taskId: number) => void;
  isClaiming?: boolean;
}

export function UnassignedRow({
  task,
  density = 'comfortable',
  onClaim,
  isClaiming,
}: UnassignedRowProps) {
  // Default path — wire to TanStack Query mutation. Tests pass `onClaim` to
  // bypass the hook (since the hook needs a wrapping ToastHost + QueryClient).
  const fallback = useClaimTask();
  const claiming = isClaiming ?? fallback.isPending;
  const fireClaim = () => {
    if (onClaim) onClaim(task.taskId);
    else fallback.mutate(task.taskId);
  };

  const cfg = priorityConfig(task.priority);
  const padding = density === 'compact' ? 'py-2' : 'py-2.5';
  const projectColor = task.customerColorHex ?? '#8E8E98';

  const badgeStyle: CSSProperties = { background: cfg.bg, color: cfg.fg };
  const dotStyle: CSSProperties = { background: projectColor };

  return (
    <div
      data-testid="unassigned-row"
      className={`grid items-center gap-3 px-4 ${padding} border-b border-ms-border-subtle last:border-0 hover:bg-ms-surface-2/40 transition-opacity duration-200 ${
        claiming ? 'opacity-50' : ''
      }`}
      style={{ gridTemplateColumns: UNASSIGNED_ROW_GRID }}
    >
      <MtPriorityBadge priority={task.priority} withLabel={false} />

      <span className="font-mono text-[10.5px] uppercase tracking-wide text-ms-text-muted truncate">
        {task.taskCode ?? `#${task.taskId}`}
      </span>

      <div className="min-w-0">
        <div className="text-[12.5px] font-medium text-ms-text-strong truncate">
          {task.taskName}
        </div>
      </div>

      <span
        className="inline-flex items-center gap-1.5 text-[10.5px] text-ms-text-secondary font-medium"
        data-testid="unassigned-row-project-chip"
      >
        <span aria-hidden="true" className="w-1.5 h-1.5 rounded-full shrink-0" style={dotStyle} />
        <span className="truncate">{task.projectName}</span>
      </span>

      <span
        className="text-[10.5px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded inline-flex items-center justify-center"
        style={badgeStyle}
      >
        {cfg.label}
      </span>

      <span className="text-[10.5px] text-ms-text-muted tabular-nums">
        {task.estimateHours ? `${task.estimateHours}h est` : '—'}
        {task.ageDays ? ` · ${task.ageDays}d` : ''}
      </span>

      <button
        type="button"
        disabled={claiming}
        onClick={fireClaim}
        aria-label={`Claim ${task.taskName}`}
        data-testid="unassigned-row-claim"
        className="px-2.5 py-1 text-[11px] font-semibold rounded-md text-white inline-flex items-center justify-center gap-1 bg-ms-orange disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <PlusIcon className="h-3 w-3" aria-hidden="true" />
        {claiming ? 'Claiming…' : 'Claim'}
      </button>
    </div>
  );
}
